import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { REPLACE_URL_PLACEHOLDER, useLoginRedirectionUrl } from "@loyalty/hooks/useLoginRedirectionUrl";
import { trackLotteryClick } from "@loyalty/utils";
import classNames from "classnames";

import { UiArrowUpSolidIcon } from "@jti/brand-icons/ui-arrow-up-solid";
import { Button } from "@jti/core-components/components/Button";
import { toast } from "@jti/core-components/components/Toaster";
import { isApiErrorWithErrorCode, translateBackendErrorCode } from "@jti/fe-common";
import { getUser, LoyaltyApi } from "@jti/rest-api";
import { type GetQrCodeDetailsLotteryOutcome } from "@jti/rest-api-types";
import { type RootStoreState } from "@jti/store/types";
import { DEPRECATED_Loading } from "@jti/ui/components/DEPRECATED_Loading";
import { Typography } from "@jti/ui/components/Typography";
import { useTranslations } from "@jti/ui/providers/useTranslations";
import { replaceStringPlaceholder } from "@jti/ui/utils/replaceStringPlaceholder";

import { ImageBackgroundContainer } from "../ImageBackgroundContainer";
import { PrizeSpinner } from "../PrizeSpinner";

import styles from "./QrCodeLottery.module.scss";

export type LotteryState = "notLogged" | "logged" | "spin" | "won" | "lost" | "alreadyParticipated";

export type LotteryStateItemData = {
  title: string;
  description: string;
  ctaLabel: string;
  ctaUrl: string;
  secondaryCtaLabel: string;
  secondaryCtaUrl: string;
};
export type LotteryStateItemPartialData = Pick<LotteryStateItemData, "ctaLabel" | "title" | "description">;
export type CtaUnhappyPathData = Omit<LotteryStateItemData, "title" | "description">;
type LotteryStateItemUnhappyPathData = Pick<LotteryStateItemData, "title" | "description">;

export type QrCodeLotteryProps = {
  generalTitle: string;
  notLogged: LotteryStateItemData;
  won: LotteryStateItemData;
  lost: LotteryStateItemUnhappyPathData;
  alreadyParticipated: LotteryStateItemUnhappyPathData;
  // logged: LotteryStateItemPartialData; // DESP-63972 logged status
  spin: LotteryStateItemPartialData;
  ctasUnhappyPath: CtaUnhappyPathData;
  backgroundImage: string;
  mobileBackgroundImage?: string;
  wheelPointsLabel: string;
  wheelLoseLabel: string;
  wheelTitle: string;
};

type ActionButtonsProps = {
  ctaLabel: string;
  ctaUrl?: string;
  secondaryCtaLabel?: string;
  secondaryCtaUrl?: string;
  onClick?: () => void;
  disabled?: boolean;
};

const ActionButtons = ({
  ctaLabel,
  ctaUrl,
  secondaryCtaLabel,
  secondaryCtaUrl,
  onClick,
  disabled,
}: ActionButtonsProps) => {
  if (!secondaryCtaLabel || !secondaryCtaUrl) {
    return (
      <Button size="md" variant="primary" onClick={onClick} disabled={disabled}>
        {ctaLabel}
      </Button>
    );
  }

  return (
    <>
      <Button size="md" variant="secondary" href={ctaUrl} disabled={disabled}>
        {ctaLabel}
      </Button>
      <Button size="md" variant="primary" href={secondaryCtaUrl} disabled={disabled}>
        {secondaryCtaLabel}
      </Button>
    </>
  );
};

export const QrCodeLottery: FC<QrCodeLotteryProps> = ({
  backgroundImage,
  mobileBackgroundImage,
  generalTitle,
  won,
  alreadyParticipated,
  lost,
  // logged, // to be used in DESP-63972
  notLogged,
  spin,
  ctasUnhappyPath,
  wheelPointsLabel,
  wheelLoseLabel,
  wheelTitle,
}) => {
  const isLogged = useSelector((state: RootStoreState) => state.loyaltyCore.isLogged);
  const [{ earnedPoints, drawnIndex }, setOption] = useState<{
    earnedPoints: number;
    drawnIndex: number | undefined;
  }>({
    earnedPoints: 0,
    drawnIndex: undefined,
  });
  const { __, dictionary } = useTranslations();
  const loginUrl = useLoginRedirectionUrl(REPLACE_URL_PLACEHOLDER);
  const [isLoading, setIsLoading] = useState(false);
  const [isHandleQrCodeCheckPending, setIsHandleQrCodeCheckPending] = useState(false);
  const [lotteryOptions, setLotteryOptions] = useState<GetQrCodeDetailsLotteryOutcome[]>([]);
  const [lotteryState, setLotteryState] = useState<LotteryState>(isLogged ? "spin" : "notLogged");
  const shouldDisableButton = isLoading || (lotteryState === "spin" && drawnIndex !== undefined);
  const qrCodeLotteryClasses = classNames(styles.wrapper, {
    [styles.fullCover]: lotteryState === "alreadyParticipated" || lotteryState === "lost",
    [styles.minHeight]: lotteryState === "logged" || lotteryState === "notLogged",
    [styles.centeredLoading]: isHandleQrCodeCheckPending,
  });
  const { ciam_token, uuid } = getUser();

  const ctasUnluckyPathProps: ActionButtonsProps = {
    ctaLabel: ctasUnhappyPath.ctaLabel,
    ctaUrl: ctasUnhappyPath.ctaUrl,
    secondaryCtaLabel: ctasUnhappyPath.secondaryCtaLabel,
    secondaryCtaUrl: ctasUnhappyPath.secondaryCtaUrl,
  };

  const lotteryOptionsLabels = useMemo(
    () =>
      lotteryOptions.map(({ points, is_win }) => {
        return is_win
          ? replaceStringPlaceholder(wheelPointsLabel, [{ placeholder: "points", value: points }], false)
          : wheelLoseLabel;
      }),
    [lotteryOptions, wheelLoseLabel, wheelPointsLabel]
  );

  const handleQrLotteryOutcome = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const qrCodeId = params.get("id");

    if (ciam_token && qrCodeId) {
      trackLotteryClick();
      setIsLoading(true);
      try {
        const { is_rewarded, earned_points, selected_outcome } = await LoyaltyApi.init().scanQrAction(
          qrCodeId,
          ciam_token,
          uuid
        );

        const optionIndex = lotteryOptions.findIndex(({ name }) => name === selected_outcome.name);

        if (!selected_outcome.is_win) {
          setOption({ earnedPoints: 0, drawnIndex: optionIndex });
        } else if (is_rewarded) {
          setOption({ earnedPoints: earned_points, drawnIndex: optionIndex });
        }

        setTimeout(() => setLotteryState(selected_outcome.is_win ? "won" : "lost"), 2000);
      } catch (error) {
        const preparedError = translateBackendErrorCode(
          isApiErrorWithErrorCode(error) ? error.error_code : undefined,
          __("loyalty.qrCodePointsCollection.error"),
          dictionary
        );
        console.error(error);
        toast.error(preparedError);
      } finally {
        setIsLoading(false);
      }
    }
  }, [__, ciam_token, lotteryOptions, uuid]);

  const buttonsConfig: Record<string | number | symbol, ActionButtonsProps> = {
    // DESP-63972 logged status
    // logged: {
    //   ctaLabel: logged.ctaLabel,
    //   onClick: () => {},
    //   disabled: false,
    // },
    notLogged: {
      ctaLabel: notLogged.ctaLabel,
      ctaUrl: notLogged.ctaUrl === REPLACE_URL_PLACEHOLDER ? loginUrl : notLogged.ctaUrl,
      secondaryCtaLabel: notLogged.secondaryCtaLabel,
      secondaryCtaUrl: notLogged.secondaryCtaUrl === REPLACE_URL_PLACEHOLDER ? loginUrl : notLogged.secondaryCtaUrl,
    },
    spin: {
      ctaLabel: spin.ctaLabel,
      onClick: handleQrLotteryOutcome,
      disabled: shouldDisableButton || !lotteryOptions.length,
    },
    won: {
      ctaLabel: won.ctaLabel,
      ctaUrl: won.ctaUrl,
      secondaryCtaLabel: won.secondaryCtaLabel,
      secondaryCtaUrl: won.secondaryCtaUrl,
    },
    lost: ctasUnluckyPathProps,
    alreadyParticipated: ctasUnluckyPathProps,
  };

  const title: Partial<Record<LotteryState, string>> = {
    // logged: logged.title, // DESP-63972 logged status
    notLogged: notLogged.title,
    spin: spin.title,
    won: replaceStringPlaceholder(won.title, [{ placeholder: "points", value: earnedPoints }], false),
    lost: lost.title,
    alreadyParticipated: alreadyParticipated.title,
  };

  const description: Partial<Record<LotteryState, string>> = {
    // logged: logged.description, // DESP-63972 logged status
    notLogged: notLogged.description,
    spin: spin.description,
    won: won.description,
    lost: lost.description,
    alreadyParticipated: alreadyParticipated.description,
  };
  const showPrizeWheel = lotteryState === "spin" || lotteryState === "won" || lotteryState === "lost";

  useEffect(() => {
    if (isLogged && lotteryState === "notLogged") {
      setLotteryState("spin");
    }
  }, [isLogged, lotteryState]);

  // using this useEffect is temporary solution before DESP-63972 is implemented
  useEffect(() => {
    const handleQrCodeCheck = async () => {
      const params = new URLSearchParams(window.location.search);
      const qrCodeId = params.get("id");

      const onError = (error: unknown = null) => {
        const preparedError = translateBackendErrorCode(
          isApiErrorWithErrorCode(error) ? error.error_code : undefined,
          __("loyalty.qrCodePointsCollection.error"),
          dictionary
        );
        toast.error(preparedError);
      };

      if (ciam_token && qrCodeId) {
        setIsHandleQrCodeCheckPending(true);
        try {
          const { is_lottery_triggered, lottery_outcomes } = await LoyaltyApi.init().getQrCodeDetails({
            ciam_token,
            uuid,
            qrCodeId,
          });

          if (is_lottery_triggered) {
            setLotteryState("alreadyParticipated");
          } else {
            setLotteryOptions(lottery_outcomes);
          }
        } catch (error) {
          console.error(error);
          setLotteryState("alreadyParticipated");
          onError(error);
        } finally {
          setIsHandleQrCodeCheckPending(false);
        }
      } else if (!qrCodeId) {
        onError();
      }
    };

    if (lotteryState === "spin") {
      handleQrCodeCheck();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lotteryState]);

  return (
    <div className={styles.qrCodeLottery}>
      <Typography variant="body2" weight="medium" className={styles.generalTitle}>
        {generalTitle}
      </Typography>
      <ImageBackgroundContainer
        background={backgroundImage}
        mobileBackground={mobileBackgroundImage}
        fullWidth={false}
        withBlur={lotteryState !== "logged" && lotteryState !== "notLogged"}
        className={styles.imageContainer}
      >
        <div className={qrCodeLotteryClasses}>
          {isHandleQrCodeCheckPending ? (
            <DEPRECATED_Loading whiteDots />
          ) : (
            <>
              <div className={styles.content}>
                <Typography className={styles.textColor} variant="h2" weight="light">
                  {title[lotteryState]}
                </Typography>
                <Typography className={styles.textColor} variant="body2" weight="light">
                  {description[lotteryState]}
                </Typography>
                <div className={styles.buttonsWrapper}>
                  <ActionButtons {...buttonsConfig[lotteryState]} />
                </div>
              </div>
              {showPrizeWheel && (
                <div
                  className={classNames(styles.wheelContainer, {
                    [styles.titlePosition]: lotteryState === "spin",
                  })}
                >
                  <PrizeSpinner
                    options={lotteryOptionsLabels}
                    drawnIndex={drawnIndex}
                    isLoading={isLoading}
                    className={styles.wheelPosition}
                    title={wheelTitle}
                  />
                  <UiArrowUpSolidIcon className={styles.icon} />
                </div>
              )}
            </>
          )}
        </div>
      </ImageBackgroundContainer>
    </div>
  );
};
